import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Thread {
  id: string;
  name: string;
  initial: string;
  status: 'live' | 'draft';
  lastMessage: string;
  time: string;
  unread: boolean;
  url: string;
}

interface ThreadMessage {
  id: number;
  role: 'ai' | 'user' | 'system' | 'card';
  text: string;
  time: string;
  cardData?: {
    type: 'preview' | 'device-tip' | 'backup';
    url?: string;
    status?: string;
    message?: string;
  };
}

const THREADS: Thread[] = [
  {
    id: 'bakery',
    name: 'Fresh Bakery',
    initial: 'FB',
    status: 'live',
    lastMessage: 'I optimized your images — 40% smaller!',
    time: '2m',
    unread: true,
    url: 'fresh-bakery.wiserwebhost.com',
  },
  {
    id: 'portfolio',
    name: 'My Portfolio',
    initial: 'MP',
    status: 'live',
    lastMessage: 'Your site is live and looking great.',
    time: '1h',
    unread: false,
    url: 'sarah-dev.wiserwebhost.com',
  },
  {
    id: 'shop',
    name: 'Vintage Shop',
    initial: 'VS',
    status: 'draft',
    lastMessage: 'Ready when you are — just say "publish".',
    time: '3h',
    unread: false,
    url: 'vintage-shop.wiserwebhost.com',
  },
];

const INITIAL_MESSAGES: ThreadMessage[] = [
  {
    id: 1,
    role: 'card',
    text: '',
    time: '',
    cardData: { type: 'preview', url: 'fresh-bakery.wiserwebhost.com', status: 'live' },
  },
  {
    id: 2,
    role: 'system',
    text: 'Backup saved: v3',
    time: '5m ago',
  },
  {
    id: 3,
    role: 'ai',
    text: 'I optimized your images — saved 40% bandwidth! Your site now loads in under 2 seconds.',
    time: '2m ago',
  },
  {
    id: 4,
    role: 'card',
    text: '',
    time: '',
    cardData: {
      type: 'device-tip',
      message: 'Content editing works great on mobile! For layout changes, try desktop.',
    },
  },
];

let msgCounter = 100;

export default function ThreadView() {
  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>(INITIAL_MESSAGES);
  const [inputVal, setInputVal] = useState('');
  const [typing, setTyping] = useState(false);
  const [bottomTab, setBottomTab] = useState<'sites' | 'chat' | 'stats' | 'profile'>('sites');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(scrollToBottom, [messages, typing, scrollToBottom]);

  const handleSend = () => {
    if (!inputVal.trim()) return;
    const text = inputVal.trim();
    setInputVal('');
    setMessages((prev) => [
      ...prev,
      { id: ++msgCounter, role: 'user', text, time: 'Just now' },
    ]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: ++msgCounter,
          role: 'system',
          text: 'Backup saved: v4',
          time: '',
        },
        {
          id: ++msgCounter,
          role: 'ai',
          text: "Done! I've made that change and saved a backup. Check the preview above to see the update.",
          time: 'Just now',
        },
      ]);
    }, 1500);
  };

  const handleQuickAction = (action: string) => {
    setMessages((prev) => [
      ...prev,
      { id: ++msgCounter, role: 'user', text: action, time: 'Just now' },
    ]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      const replies: Record<string, string> = {
        'Edit Text': "Which page would you like to edit? I can update any text on your site — just tell me what to change.",
        'Change Photo': "Upload a new photo and tell me where to place it, or describe what you need and I'll find a stock image.",
        'Settings': "Here's what you can configure: custom domain, SEO title & description, favicon, analytics tracking. What would you like to change?",
        'View Stats': "Your site had 142 visitors this week, up 23% from last week! Top pages: Home (67%), Menu (24%), Contact (9%).",
      };
      setMessages((prev) => [
        ...prev,
        {
          id: ++msgCounter,
          role: 'ai',
          text: replies[action] || "I'll work on that for you!",
          time: 'Just now',
        },
      ]);
    }, 1000);
  };

  // --- Thread list (messaging app style) ---
  const renderThreadList = () => (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Your Sites</h1>
        <Link to="/mockups" className="text-zinc-500 hover:text-zinc-300 transition">
          <span className="material-icons text-lg">arrow_back</span>
        </Link>
      </div>

      {/* Search */}
      <div className="px-4 pb-3">
        <div className="flex items-center bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-3">
          <span className="material-icons text-zinc-600 text-lg">search</span>
          <input
            type="text"
            placeholder="Search sites..."
            className="flex-1 bg-transparent py-2 px-2 text-sm text-white placeholder:text-zinc-600 outline-none"
          />
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {THREADS.map((thread) => (
          <button
            key={thread.id}
            onClick={() => setActiveThread(thread.id)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/40 transition border-b border-zinc-800/30"
          >
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center text-sm font-bold text-orange-400 shrink-0 relative">
              {thread.initial}
              {thread.status === 'live' && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-base" />
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between">
                <p className={`text-sm ${thread.unread ? 'font-semibold text-white' : 'text-zinc-300'}`}>
                  {thread.name}
                </p>
                <span className="text-[10px] text-zinc-600 shrink-0">{thread.time}</span>
              </div>
              <p className="text-xs text-zinc-500 truncate mt-0.5">
                <span className="material-icons text-[10px] align-middle mr-0.5">smart_toy</span>
                {thread.lastMessage}
              </p>
            </div>
            {thread.unread && (
              <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" />
            )}
          </button>
        ))}

        {/* New site button */}
        <button className="w-full flex items-center gap-3 px-4 py-3 text-orange-400 hover:bg-orange-500/5 transition">
          <div className="w-11 h-11 rounded-full border-2 border-dashed border-orange-500/40 flex items-center justify-center">
            <span className="material-icons text-lg">add</span>
          </div>
          <span className="text-sm font-medium">New Site</span>
        </button>
      </div>
    </div>
  );

  // --- Thread conversation ---
  const renderThread = () => {
    const thread = THREADS.find((t) => t.id === activeThread);
    if (!thread) return null;

    return (
      <div className="flex-1 flex flex-col">
        {/* Thread header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 shrink-0">
          <button
            onClick={() => setActiveThread(null)}
            className="text-zinc-400 hover:text-white transition"
          >
            <span className="material-icons">arrow_back</span>
          </button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center text-xs font-bold text-orange-400">
            {thread.initial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">{thread.name}</p>
            <p className="text-[10px] text-zinc-500">
              {thread.status === 'live' ? '● Live' : '○ Draft'} · {thread.url}
            </p>
          </div>
          <button className="text-zinc-500 hover:text-zinc-300 transition">
            <span className="material-icons text-lg">open_in_new</span>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((m) => {
            // System message
            if (m.role === 'system') {
              return (
                <div key={m.id} className="flex justify-center">
                  <span className="text-[10px] text-zinc-600 bg-zinc-800/40 px-2.5 py-0.5 rounded-full">
                    {m.text}
                  </span>
                </div>
              );
            }

            // Card: preview, device-tip, backup
            if (m.role === 'card' && m.cardData) {
              if (m.cardData.type === 'preview') {
                return (
                  <div key={m.id} className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-3">
                    <div className="h-20 bg-gradient-to-br from-amber-800/20 to-orange-700/20 rounded-lg flex items-center justify-center mb-2">
                      <span className="text-sm font-bold text-orange-200/50">{thread.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="material-icons text-green-400 text-xs">lock</span>
                        <span className="text-[11px] text-zinc-400 font-mono">{m.cardData.url}</span>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                        {m.cardData.status}
                      </span>
                    </div>
                  </div>
                );
              }

              if (m.cardData.type === 'device-tip') {
                return (
                  <div
                    key={m.id}
                    className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-3 flex items-start gap-2"
                  >
                    <span className="material-icons text-sky-400 text-lg shrink-0">phone_iphone</span>
                    <p className="text-xs text-sky-300/80">{m.cardData.message}</p>
                  </div>
                );
              }
            }

            // Regular message
            return (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className="max-w-[85%]">
                  <div
                    className={`rounded-2xl px-3 py-2 ${
                      m.role === 'user'
                        ? 'bg-orange-500/15 border border-orange-500/30 rounded-br-md'
                        : 'bg-zinc-800/60 border border-zinc-700/50 rounded-bl-md'
                    }`}
                  >
                    {m.role === 'ai' && (
                      <span className="material-icons text-orange-400 text-[10px] mr-0.5 align-middle">
                        smart_toy
                      </span>
                    )}
                    <span
                      className={`text-sm ${
                        m.role === 'user' ? 'text-orange-200' : 'text-zinc-300'
                      }`}
                    >
                      {m.text}
                    </span>
                  </div>
                  {m.time && <p className="text-[9px] text-zinc-700 mt-0.5 px-1">{m.time}</p>}
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="flex justify-start">
              <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" />
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Quick actions */}
        <div className="px-4 py-2 flex gap-2 overflow-x-auto shrink-0 border-t border-zinc-800/50">
          {['Edit Text', 'Change Photo', 'Settings', 'View Stats'].map((action) => (
            <button
              key={action}
              onClick={() => handleQuickAction(action)}
              className="shrink-0 px-3 py-1.5 bg-zinc-800/60 border border-zinc-700/50 rounded-full text-xs text-zinc-400 hover:text-orange-300 hover:border-orange-500/40 transition"
            >
              {action}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="p-3 shrink-0">
          <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-3">
            <input
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none"
            />
            <button onClick={handleSend} className="text-orange-400 hover:text-orange-300 transition">
              <span className="material-icons text-lg">send</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-base flex flex-col max-w-lg mx-auto border-x border-zinc-800/40">
      {/* Main content — either list or thread */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeThread ? renderThread() : renderThreadList()}
      </div>

      {/* Bottom tab bar */}
      {!activeThread && (
        <div className="flex border-t border-zinc-800 shrink-0 bg-zinc-950/80">
          {(
            [
              { id: 'sites', icon: 'home', label: 'Sites' },
              { id: 'chat', icon: 'chat', label: 'Chat' },
              { id: 'stats', icon: 'analytics', label: 'Stats' },
              { id: 'profile', icon: 'person', label: 'Profile' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setBottomTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 transition ${
                bottomTab === tab.id ? 'text-orange-400' : 'text-zinc-600'
              }`}
            >
              <span className="material-icons text-lg">{tab.icon}</span>
              <span className="text-[10px]">{tab.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
